import type * as THREE from 'three';

export interface MinimapTarget {
  readonly position: THREE.Vector3;
  readonly color: string;
  readonly label?: string;
}

/** A radar blip drawn as a coloured dot. Distinct from MinimapTarget so we
 *  can render dozens of them without label clutter. */
export interface MinimapBlip {
  readonly position: THREE.Vector3;
  readonly color: string;
}

export type MinimapCelestialKind = 'star' | 'planet' | 'moon' | 'station';

/** Background context elements (star, planets, moons, station) drawn under
 *  the tactical NPC layer. Each kind gets a distinct shape/size so the
 *  player can read the system layout at a glance. */
export interface MinimapCelestial {
  readonly position: THREE.Vector3;
  readonly kind: MinimapCelestialKind;
  /** CSS colour string. Caller converts the body's material colour. */
  readonly color: string;
}

export interface MinimapUpdateOptions {
  /** World-space radius shown by the radar. Defaults to the layer radius
   *  (220u for the surface map). Space combat needs a much wider sweep. */
  readonly radius?: number;
  /** Optional anchor target rendered with a label and arrow when off-screen. */
  readonly target?: MinimapTarget | null;
  /** Optional mission objective target rendered with a distinct halo +
   *  diamond glyph so the player can tell it apart from a manually
   *  picked anchor. Coexists with `target`. */
  readonly missionTarget?: MinimapTarget | null;
  /** Cluster of unlabelled blips (NPCs). */
  readonly blips?: readonly MinimapBlip[];
  /** Background system elements (star, planets, moons, station). */
  readonly celestials?: readonly MinimapCelestial[];
}

// 2D canvas redraws are cheap individually but they still synchronously stall
// the main loop and trigger DOM compositing — running the minimap at 25 Hz
// instead of 60 is invisible to the player and frees ~1.5 ms per second of
// frame budget on top of the saved draw calls.
const MINIMAP_REDRAW_INTERVAL_MS = 40;

export class MinimapLayer {
  readonly element: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly size = 180;
  private readonly defaultRadius = 220;
  private visible = false;
  private lastRedrawAt = 0;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'minimap-layer';

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'minimap-canvas';

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = this.size * dpr;
    this.canvas.height = this.size * dpr;
    this.canvas.style.width = `${this.size}px`;
    this.canvas.style.height = `${this.size}px`;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Minimap 2D context unavailable');
    this.ctx = ctx;
    this.ctx.scale(dpr, dpr);

    this.element.append(this.canvas);
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('is-visible');
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('is-visible');
  }

  update(
    playerX: number,
    playerZ: number,
    playerYaw: number,
    payload: MinimapTarget | MinimapUpdateOptions
  ): void {
    if (!this.visible) return;

    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.lastRedrawAt < MINIMAP_REDRAW_INTERVAL_MS) return;
    this.lastRedrawAt = now;

    const opts = normalizePayload(payload);
    const radius = opts.radius ?? this.defaultRadius;

    const ctx = this.ctx;
    const size = this.size;
    const half = size / 2;

    ctx.clearRect(0, 0, size, size);

    ctx.fillStyle = 'rgba(14, 16, 48, 0.72)';
    ctx.beginPath();
    ctx.arc(half, half, half - 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(156, 205, 255, 0.55)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(half, half, half - 2, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(156, 205, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(half, 8);
    ctx.lineTo(half, size - 8);
    ctx.moveTo(8, half);
    ctx.lineTo(size - 8, half);
    ctx.stroke();

    ctx.fillStyle = 'rgba(156, 205, 255, 0.55)';
    ctx.font = 'bold 10px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('N', half, 6);

    const scale = (half - 12) / radius;

    // Celestials render first so the tactical layer (NPCs, target arrow,
    // player indicator) always sits on top and reads cleanly even when the
    // player is sitting on a planet's position on the map.
    if (opts.celestials) {
      for (const body of opts.celestials) {
        this.drawCelestial(half, body, playerX, playerZ, scale, radius);
      }
    }

    if (opts.blips) {
      for (const blip of opts.blips) {
        this.drawBlip(half, blip, playerX, playerZ, scale, radius);
      }
    }

    if (opts.target) {
      this.drawTarget(size, half, opts.target, playerX, playerZ, scale, radius);
    }

    if (opts.missionTarget) {
      this.drawMissionTarget(
        size,
        half,
        opts.missionTarget,
        playerX,
        playerZ,
        scale,
        radius
      );
    }

    ctx.save();
    ctx.translate(half, half);
    ctx.rotate(-playerYaw);
    ctx.fillStyle = 'rgba(156, 205, 255, 1)';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(0, -9);
    ctx.lineTo(6.5, 6);
    ctx.lineTo(0, 3);
    ctx.lineTo(-6.5, 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /** Tiny dot for an NPC blip. Out-of-range blips are clamped to the rim
   *  so the player can still tell where threats are. */
  private drawBlip(
    half: number,
    blip: MinimapBlip,
    playerX: number,
    playerZ: number,
    scale: number,
    radius: number
  ): void {
    const dx = blip.position.x - playerX;
    const dz = blip.position.z - playerZ;
    const distance = Math.sqrt(dx * dx + dz * dz);

    let mx: number;
    let mz: number;
    let onEdge = false;
    if (distance > radius) {
      const edge = (half - 5) / distance;
      mx = dx * edge;
      mz = dz * edge;
      onEdge = true;
    } else {
      mx = dx * scale;
      mz = dz * scale;
    }

    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = blip.color;
    ctx.shadowColor = blip.color;
    ctx.shadowBlur = onEdge ? 3 : 4;
    ctx.beginPath();
    ctx.arc(half + mx, half + mz, onEdge ? 1.6 : 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** Draw a celestial body marker. Different kinds get different shapes so
   *  the player can read the disc without a legend — star is a pointed
   *  glyph, station is a diamond, planets/moons are filled discs at
   *  different sizes. Off-disc bodies clamp to the rim like blips. */
  private drawCelestial(
    half: number,
    body: MinimapCelestial,
    playerX: number,
    playerZ: number,
    scale: number,
    radius: number
  ): void {
    const dx = body.position.x - playerX;
    const dz = body.position.z - playerZ;
    const distance = Math.sqrt(dx * dx + dz * dz);

    let mx: number;
    let mz: number;
    let onEdge = false;
    if (distance > radius) {
      const edge = (half - 4) / distance;
      mx = dx * edge;
      mz = dz * edge;
      onEdge = true;
    } else {
      mx = dx * scale;
      mz = dz * scale;
    }

    const ctx = this.ctx;
    ctx.save();
    const x = half + mx;
    const y = half + mz;

    switch (body.kind) {
      case 'star':
        // 4-pointed cardinal star with a soft glow so it's the dominant
        // background marker.
        ctx.fillStyle = body.color;
        ctx.shadowColor = body.color;
        ctx.shadowBlur = onEdge ? 5 : 9;
        this.drawStarGlyph(x, y, onEdge ? 2.8 : 4.2);
        break;
      case 'planet':
        ctx.fillStyle = body.color;
        ctx.shadowColor = body.color;
        ctx.shadowBlur = onEdge ? 2 : 4;
        ctx.beginPath();
        ctx.arc(x, y, onEdge ? 2 : 3.2, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'moon':
        ctx.fillStyle = body.color;
        ctx.globalAlpha = 0.75;
        ctx.shadowColor = body.color;
        ctx.shadowBlur = onEdge ? 1 : 2;
        ctx.beginPath();
        ctx.arc(x, y, onEdge ? 1.3 : 1.9, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'station':
        // Diamond outline — the only built structure, gets a distinct shape
        // so it never blends into the planetary discs.
        ctx.strokeStyle = body.color;
        ctx.fillStyle = body.color;
        ctx.shadowColor = body.color;
        ctx.shadowBlur = onEdge ? 3 : 5;
        ctx.lineWidth = 1.2;
        this.drawDiamond(x, y, onEdge ? 2.4 : 3.4);
        break;
    }
    ctx.restore();
  }

  private drawStarGlyph(cx: number, cy: number, r: number): void {
    const ctx = this.ctx;
    const inner = r * 0.42;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + inner, cy - inner);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx + inner, cy + inner);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - inner, cy + inner);
    ctx.lineTo(cx - r, cy);
    ctx.lineTo(cx - inner, cy - inner);
    ctx.closePath();
    ctx.fill();
  }

  private drawDiamond(cx: number, cy: number, r: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r, cy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  /** Anchor target — renders a labelled dot inside the disc, or an arrow
   *  on the rim with the world-space distance when out of range. */
  private drawTarget(
    size: number,
    half: number,
    target: MinimapTarget,
    playerX: number,
    playerZ: number,
    scale: number,
    radius: number
  ): void {
    const dx = target.position.x - playerX;
    const dz = target.position.z - playerZ;
    const distance = Math.sqrt(dx * dx + dz * dz);

    const ctx = this.ctx;
    if (distance > radius) {
      const edge = (half - 14) / distance;
      const ex = dx * edge;
      const ez = dz * edge;

      const angle = Math.atan2(ez, ex);
      ctx.save();
      ctx.translate(half + ex, half + ez);
      ctx.rotate(angle);
      ctx.fillStyle = target.color;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(9, 0);
      ctx.lineTo(-5, -5);
      ctx.lineTo(-5, 5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      ctx.fillStyle = 'rgba(255, 211, 137, 0.95)';
      ctx.font = '11px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`${Math.round(distance)} m`, half, size - 8);
    } else {
      const mx = dx * scale;
      const mz = dz * scale;
      ctx.save();
      ctx.fillStyle = target.color;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(half + mx, half + mz, 5.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      if (target.label) {
        ctx.fillStyle = 'rgba(255, 211, 137, 0.9)';
        ctx.font = '10px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(target.label, half + mx, half + mz + 8);
      }
    }
  }

  /** Mission objective marker — same projection logic as drawTarget but
   *  rendered with a glowing diamond + soft halo so the player can read
   *  it as the primary objective even when a manual target is also on
   *  the disc. */
  private drawMissionTarget(
    size: number,
    half: number,
    target: MinimapTarget,
    playerX: number,
    playerZ: number,
    scale: number,
    radius: number
  ): void {
    const dx = target.position.x - playerX;
    const dz = target.position.z - playerZ;
    const distance = Math.sqrt(dx * dx + dz * dz);

    const ctx = this.ctx;
    if (distance > radius) {
      const edge = (half - 14) / distance;
      const ex = dx * edge;
      const ez = dz * edge;
      const angle = Math.atan2(ez, ex);

      ctx.save();
      ctx.translate(half + ex, half + ez);
      ctx.rotate(angle);
      ctx.fillStyle = target.color;
      ctx.shadowColor = target.color;
      ctx.shadowBlur = 8;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(10, 0);
      ctx.lineTo(-5, -6);
      ctx.lineTo(-3, 0);
      ctx.lineTo(-5, 6);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      ctx.fillStyle = target.color;
      ctx.font = 'bold 10px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`◆ ${Math.round(distance)} m`, half, size - 8);
    } else {
      const mx = dx * scale;
      const mz = dz * scale;
      ctx.save();
      // Pulsing halo ring.
      const t = (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 480;
      const pulse = 0.55 + 0.45 * Math.sin(t);
      ctx.globalAlpha = 0.35 + 0.25 * pulse;
      ctx.fillStyle = target.color;
      ctx.shadowColor = target.color;
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(half + mx, half + mz, 8 + pulse * 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      // Diamond core.
      ctx.fillStyle = target.color;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = 1.4;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(half + mx, half + mz - 5);
      ctx.lineTo(half + mx + 5, half + mz);
      ctx.lineTo(half + mx, half + mz + 5);
      ctx.lineTo(half + mx - 5, half + mz);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();

      if (target.label) {
        ctx.fillStyle = target.color;
        ctx.font = 'bold 10px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(`◆ ${target.label}`, half + mx, half + mz + 9);
      }
    }
  }
}

function normalizePayload(
  payload: MinimapTarget | MinimapUpdateOptions
): MinimapUpdateOptions {
  // Distinguish the legacy single-target shape from the new options bag by
  // looking for a Vector3 `position` field — only MinimapTarget has it.
  if ('position' in payload && payload.position) {
    return { target: payload as MinimapTarget };
  }
  return payload as MinimapUpdateOptions;
}
